import { useMemo } from "react";
import SearchFilterInput from "../../form-fields/search-filter-input";
import SimpleButton from "../../buttons/Simple-button";
import H2 from "../../typography/H2";
import SortIcon from "../../../assets/svgs/SortIcon";
import "./top-listing-headers.css";
import SingleDropdown from "../../singleDropdown/SingleDropdown";
import Chevren_downIcon from "../../../assets/svgs/Chevren_down";

interface ListingTopHeaderProps {
  PageHeading: string;
  className?: string;
  ButtonName?: string;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  onClick?: () => void;
  handleSort: () => void;
  currentSortDirection: string;
  handleSelectChange: (value: string, label: string) => void;
  filterOptions?: { title: string; count: number }[];
  secondaryFilterOptions?: { title: string; count: number }[];
  activeTab: string;
  setActiveTab: (tab: string) => void;
  selectedFilterLabel?: string;
  totalItemCount?: number;
  showAddButton?: boolean;
}

export default function TenantListingTopHeader({
  PageHeading,
  className = "",
  ButtonName,
  searchTerm,
  setSearchTerm,
  onClick,
  handleSort,
  currentSortDirection,
  handleSelectChange,
  activeTab,
  setActiveTab,
  filterOptions = [],
  secondaryFilterOptions = [],
  selectedFilterLabel = "All",
  totalItemCount,
  showAddButton = true,
}: ListingTopHeaderProps) {
  const effectiveFilterLabel = selectedFilterLabel || "All";

  const activeSecondaryFilter = useMemo(
    () => secondaryFilterOptions.find((filter) => filter.title === effectiveFilterLabel),
    [secondaryFilterOptions, effectiveFilterLabel],
  );

  // `activeSecondaryFilter` is `undefined` either because the selection is "All" (no matching
  // synthetic option in some callers) or because a non-"All" option's count dropped to 0 and
  // zero-count suppression hid it. Only fall back to `totalItemCount` in the former case --
  // falling back to it for a hidden non-"All" label would show the *tab* total next to a
  // specific status label (e.g. "Lease Expired (137)" when there are zero expired leases).
  const triggerCount = activeSecondaryFilter?.count ?? (effectiveFilterLabel === "All" ? totalItemCount ?? 0 : 0);

  return (
    <div className={`top-listing-header ${className}`}>
      <div className="top-listing-header-top">
        <H2 text={`${PageHeading || ""}`} />
        {ButtonName && showAddButton && <SimpleButton label={ButtonName} onClick={onClick} />}
      </div>

      <div className="top-listing-header-filters">
        <div className="filter-options">
          {filterOptions.map((option, index) => (
            <button
              key={index}
              className={`filter-option${
                activeTab === option.title ? " filter-option-active" : ""
              }`}
              onClick={() => setActiveTab(option.title)}
            >
              {option.title} ({option.count})
            </button>
          ))}
        </div>

        <div className="search-sort-container">
          <SearchFilterInput
            placeholder="Search By Tenant Name"
            searchTerm={searchTerm}
            handleSearch={(e) => setSearchTerm(e.target.value)}
          />

          <SingleDropdown
            key={`${activeTab}-${selectedFilterLabel}`} // Force re-render on tab/filter change
            triggerText={`${effectiveFilterLabel} (${triggerCount})`}
            triggerIcon={<Chevren_downIcon width={"18"} height={"18"} />}
            triggerIconPosition="right"
            menuItems={secondaryFilterOptions.map(filter => ({
              label: `${filter.title} (${filter.count})`,
              action: () => handleSelectChange(filter.title, filter.title),
            }))}
          />

          <div className="sort-button" onClick={handleSort} role="button" tabIndex={0}>
            <span className="sort-text">
              {currentSortDirection === "asc"
                ? "Sort by: A-Z"
                : "Sort by: Z-A"}
            </span>
            <SortIcon />
          </div>
        </div>
      </div>
    </div>
  );
}